import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, ChevronLeft, ChevronRight,
  Image as ImageIcon, Star
} from 'lucide-react';
import Navbar from '../Dashboard/NavBar';
import DashboardHeader from '../Dashboard/Header';
import Avatar from '../../Assets/avatar.png';
import { SafeMathText } from './MathRenderer';
import { auth } from '../../firebase';
import {
  createQuiz,
  updateQuiz,
  getQuizById,
  getCategoryById,
  getCategories
} from '../../services/firestoreService';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigation } from '../../contexts/NavigationContext';
import LoadingOverlay from '../Common/LoadingOverlay';

// Simplified 3-step wizard
const STEPS = {
  BASICS: 0,
  QUESTIONS: 1,
  REVIEW: 2
};

/**
 * Calculate score for multiple-choice question with multiple correct answers
 * @param {Object} question - Question object with partialCreditEnabled, partialCreditMode, options, points
 * @param {Array<string>} selectedAnswerIds - Array of selected option IDs
 * @returns {number} - Calculated score
 */
export function calculateMultipleChoiceScore(question, selectedAnswerIds) {
  // If single-choice question (only one correct answer allowed)
  if (!question.allowMultipleAnswers) {
    const isCorrect = selectedAnswerIds.length === 1 &&
                     selectedAnswerIds.some(id => {
                       const selected = question.options.find(opt => opt.id === id);
                       return selected && selected.isCorrect;
                     });
    return isCorrect ? question.points : 0;
  }

  // Multiple-choice with multiple correct answers
  const correctOptions = question.options.filter(opt => opt.isCorrect);
  const totalCorrect = correctOptions.length;

  // Count correct and incorrect selections
  const correctSelected = selectedAnswerIds.filter(id =>
    question.options.find(opt => opt.id === id && opt.isCorrect)
  ).length;

  const incorrectSelected = selectedAnswerIds.filter(id =>
    question.options.find(opt => opt.id === id && !opt.isCorrect)
  ).length;

  // PHASE 1: All-or-nothing (default)
  if (!question.partialCreditEnabled) {
    const isFullyCorrect = correctSelected === totalCorrect && incorrectSelected === 0;
    return isFullyCorrect ? question.points : 0;
  }

  // PHASE 2: Partial credit modes
  if (question.partialCreditMode === 'proportional') {
    // Award points proportional to correct selections, no points if any incorrect selected
    if (incorrectSelected > 0) return 0;
    return Math.round((correctSelected / totalCorrect) * question.points);
  }

  if (question.partialCreditMode === 'penalty') {
    // Award points for correct, subtract for incorrect
    // Formula: max(0, ((correct - incorrect) / totalCorrect) * points)
    const netScore = ((correctSelected - incorrectSelected) / totalCorrect) * question.points;
    return Math.max(0, Math.round(netScore));
  }

  return 0; // Fallback
}

function MakeQuiz({ isOpen = false, onClose = null, initialData = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotifications();
  const { setNavigationBlock } = useNavigation();
  const [searchParams] = useSearchParams();
  const categoryIdFromUrl = initialData?.categoryId || searchParams.get('categoryId');
  const quizId = initialData?.quizId || searchParams.get('quizId');

  const isModalMode = isOpen && onClose;

  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true); // Loading state for initial data fetch
  const [isEditMode, setIsEditMode] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [isPublished, setIsPublished] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(STEPS.BASICS);

  // Form data - DEFAULT TO PRIVATE
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryIdFromUrl || '');
  const [category, setCategory] = useState(null);
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [isPublic, setIsPublic] = useState(false); // DEFAULT: PRIVATE
  // isDraft is managed in handleSaveQuiz - not needed in state here

  // Settings
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [timeLimit, setTimeLimit] = useState(30);
  const [navigationType, setNavigationType] = useState('free');
  const [reviewPermission, setReviewPermission] = useState('full');
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [randomizeOptions, setRandomizeOptions] = useState(false); // Global setting for all MC questions
  const [attemptLimit, setAttemptLimit] = useState('unlimited'); // 'unlimited' | 'once' | number
  const [maxAttempts, setMaxAttempts] = useState(3); // Custom number of attempts
  const [hasPassingScore, setHasPassingScore] = useState(false); // Enable/disable passing score
  const [passingScore, setPassingScore] = useState(70); // Minimum percentage to pass (default 70%)

  // Questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([
    {
      id: Date.now(),
      type: 'multiple-choice', // NEW: Question type (multiple-choice | short-answer | true-false)
      questionText: '',
      imageUrl: '',
      points: 1, // Default 1 point per question
      explanation: '', // NEW: Optional explanation
      hint: '', // NEW: Optional hint to help students

      // Multiple Choice fields
      allowMultipleAnswers: false,
      partialCreditEnabled: false, // NEW: Partial credit toggle
      partialCreditMode: 'proportional', // NEW: 'proportional' | 'penalty'
      randomizeOptions: false, // NEW: Shuffle options for each student
      options: [
        { id: Date.now() + 1, text: '', isCorrect: true },
        { id: Date.now() + 2, text: '', isCorrect: false }
      ],

      // Short Answer fields
      correctAnswers: [
        { value: '', isPrimary: true }
      ],
      matchingMode: 'exact-case-insensitive',

      // True/False fields
      correctAnswer: null // true | false | null
    }
  ]);

  // Validation errors (inline, no notifications)
  const [errors, setErrors] = useState({});

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  // Calculate total points
  const totalPoints = questions.reduce((sum, q) => sum + (parseInt(q.points) || 0), 0);

  // Check if quiz has any content
  // const hasQuizContent = () => {
  //   return (
  //     quizTitle.trim() !== '' ||
  //     quizDescription.trim() !== '' ||
  //     coverImage !== '' ||
  //     selectedCategoryId !== '' ||
  //     questions.some(q => q.questionText.trim() !== '' || q.options.some(opt => opt.text.trim() !== ''))
  //   );
  // };

  // Update navigation block state whenever content changes
  useEffect(() => {
    const hasContent = (
      quizTitle.trim() !== '' ||
      quizDescription.trim() !== '' ||
      coverImage !== '' ||
      selectedCategoryId !== '' ||
      questions.some(q => q.questionText.trim() !== '' || q.options.some(opt => opt.text.trim() !== ''))
    );

    setNavigationBlock(hasContent);

    // Cleanup: clear navigation block when component unmounts
    return () => {
      setNavigationBlock(false);
    };
  }, [quizTitle, quizDescription, coverImage, selectedCategoryId, questions, setNavigationBlock]);

  // Custom navigation guard using popstate for back/forward
  useEffect(() => {
    const handlePopState = (e) => {
      const hasContent = (
        quizTitle.trim() !== '' ||
        quizDescription.trim() !== '' ||
        coverImage !== '' ||
        selectedCategoryId !== '' ||
        questions.some(q => q.questionText.trim() !== '' || q.options.some(opt => opt.text.trim() !== ''))
      );

      if (hasContent) {
        e.preventDefault();
        const confirmLeave = window.confirm('You have unsaved changes. Do you really want to leave?');
        if (!confirmLeave) {
          // Push current location back to prevent navigation
          window.history.pushState(null, '', location.pathname + location.search);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [quizTitle, quizDescription, coverImage, selectedCategoryId, questions, location]);

  // Warn before leaving page with unsaved changes (browser actions)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasContent = (
        quizTitle.trim() !== '' ||
        quizDescription.trim() !== '' ||
        coverImage !== '' ||
        selectedCategoryId !== '' ||
        questions.some(q => q.questionText.trim() !== '' || q.options.some(opt => opt.text.trim() !== ''))
      );

      if (hasContent) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [quizTitle, quizDescription, coverImage, selectedCategoryId, questions]);

  // Get badge state based on quiz status
  const getBadgeState = () => {
    if (isEditMode) {
      return { icon: '✏️', text: 'Edit', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)', color: '#2563EB' };
    }
    // Default: Draft mode
    return { icon: '✨', text: 'Draft', bg: '#F8FAFC', border: '#E2E8F0', color: '#64748B' };
  };

  // Load categories and quiz data
  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true); // Start loading
      try {
        // Get only current user's categories
        const userId = auth.currentUser?.uid;
        const categoriesResult = await getCategories(userId);
        if (categoriesResult.success) {
          setAllCategories(categoriesResult.data);
        }

        // Load quiz data first if editing
        if (quizId) {
          setIsEditMode(true);
          const quizResult = await getQuizById(quizId);
          if (quizResult.success) {
            const quiz = quizResult.data;
            setQuizTitle(quiz.title || '');
            setQuizDescription(quiz.description || '');
            setCoverImage(quiz.image || '');
            setIsPublic(quiz.isPublic !== undefined ? quiz.isPublic : false);

            // Set the category from the quiz
            if (quiz.category) {
              setSelectedCategoryId(quiz.category);
              // Load the category details
              const categoryResult = await getCategoryById(quiz.category);
              if (categoryResult.success) {
                setCategory(categoryResult.data);
              }
            }

            // Check if quiz is published (not a draft)
            if (quiz.isDraft === false) {
              setIsPublished(true);
            }

            if (quiz.settings) {
              setHasTimeLimit(!!quiz.settings.timeLimit);
              setTimeLimit(quiz.settings.timeLimit || 30);
              setNavigationType(quiz.settings.navigationType || 'free');
              setReviewPermission(quiz.settings.reviewPermission || 'full');
              setShuffleQuestions(quiz.settings.shuffleQuestions || false);

              if (quiz.settings.randomizeOptions !== undefined) {
                setRandomizeOptions(quiz.settings.randomizeOptions);
              }

              // Load attempt limit settings
              if (quiz.settings.attemptLimit !== undefined) {
                setAttemptLimit(quiz.settings.attemptLimit);
              }
              if (quiz.settings.maxAttempts !== undefined) {
                setMaxAttempts(quiz.settings.maxAttempts);
              }
              // Load passing score settings
              if (quiz.settings.hasPassingScore !== undefined) {
                setHasPassingScore(quiz.settings.hasPassingScore);
              }
              if (quiz.settings.passingScore !== undefined) {
                setPassingScore(quiz.settings.passingScore);
              }
            }

            if (quiz.questions && quiz.questions.length > 0) {
              const loadedQuestions = quiz.questions.map((q, idx) => {
                const baseQuestion = {
                  id: Date.now() + idx,
                  type: q.type || 'multiple-choice',
                  questionText: q.questionText || '',
                  imageUrl: q.imageUrl || '',
                  points: q.points || 1,
                  explanation: q.explanation || '',
                  hint: q.hint || ''
                };

                // Load type-specific fields
                if (baseQuestion.type === 'multiple-choice') {
                  return {
                    ...baseQuestion,
                    allowMultipleAnswers: q.allowMultipleAnswers || false,
                    partialCreditEnabled: q.partialCreditEnabled || false,
                    partialCreditMode: q.partialCreditMode || 'proportional',
                    randomizeOptions: q.randomizeOptions || false,
                    options: q.options?.map((opt, optIdx) => ({
                      id: Date.now() + idx * 100 + optIdx,
                      text: opt.text || '',
                      isCorrect: opt.isCorrect || false
                    })) || []
                  };
                } else if (baseQuestion.type === 'short-answer') {
                  return {
                    ...baseQuestion,
                    correctAnswers: q.correctAnswers?.map((a, aIdx) => ({
                      value: a.value || '',
                      isPrimary: a.isPrimary || false
                    })) || [{ value: '', isPrimary: true }],
                    matchingMode: q.matchingMode || 'exact-case-insensitive'
                  };
                } else if (baseQuestion.type === 'true-false') {
                  return {
                    ...baseQuestion,
                    correctAnswer: q.correctAnswer !== undefined ? q.correctAnswer : null
                  };
                }

                return baseQuestion;
              });
              setQuestions(loadedQuestions);
            }
          }
        } else if (selectedCategoryId) {
          // If not editing but have a categoryId (from URL), load that category
          const categoryResult = await getCategoryById(selectedCategoryId);
          if (categoryResult.success) {
            setCategory(categoryResult.data);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setDataLoading(false); // End loading
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]); // Only reload when quizId changes, not categoryId (selectedCategoryId intentionally excluded)

  // Validation Modal State
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Question Validation Helper Functions
  const isQuestionValid = (question) => {
    const errors = [];

    // Check question text
    if (!question.questionText || question.questionText.trim() === '') {
      errors.push('Question text is required');
    }

    // Check points
    const points = parseInt(question.points);
    if (isNaN(points) || points < 1 || points > 10) {
      errors.push('Points must be between 1 and 10');
    }

    // Type-specific validation
    if (question.type === 'multiple-choice') {
      // Check options
      if (!question.options || question.options.length < 2) {
        errors.push('At least 2 answer options required');
      } else {
        // Check if all options have text
        const emptyOptions = question.options.filter(opt => !opt.text || opt.text.trim() === '');
        if (emptyOptions.length > 0) {
          errors.push(`${emptyOptions.length} option(s) have empty text`);
        }
        // Check if at least one correct answer
        const hasCorrect = question.options.some(opt => opt.isCorrect);
        if (!hasCorrect) {
          errors.push('At least one correct answer must be selected');
        }
      }
    } else if (question.type === 'short-answer') {
      // Check correct answers
      if (!question.correctAnswers || question.correctAnswers.length === 0) {
        errors.push('At least one correct answer required');
      } else {
        const emptyAnswers = question.correctAnswers.filter(a => !a.value || a.value.trim() === '');
        if (emptyAnswers.length === question.correctAnswers.length) {
          errors.push('At least one correct answer must have text');
        }
      }
    } else if (question.type === 'true-false') {
      // Check if answer selected
      if (question.correctAnswer === null || question.correctAnswer === undefined) {
        errors.push('Correct answer (True or False) must be selected');
      }
    }

    return { valid: errors.length === 0, errors };
  };

  const validateAllQuestions = () => {
    const allErrors = [];

    questions.forEach((q, idx) => {
      const validation = isQuestionValid(q);
      if (!validation.valid) {
        allErrors.push({
          questionNumber: idx + 1,
          questionId: q.id,
          errors: validation.errors
        });
      }
    });

    return allErrors;
  };

  // Navigation
  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const goToNextStep = () => {
    // Validate current step
    if (currentStep === STEPS.BASICS) {
      const newErrors = {};

      if (!selectedCategoryId) {
        newErrors.category = 'Please select a category';
      }
      if (!quizTitle.trim()) {
        newErrors.title = 'Quiz title is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
    }

    // Validate questions before moving to Part 3 (Review)
    if (currentStep === STEPS.QUESTIONS) {
      const questionErrors = validateAllQuestions();

      if (questionErrors.length > 0) {
        setValidationErrors(questionErrors);
        setShowValidationModal(true);
        return;
      }
    }

    if (currentStep < STEPS.REVIEW) {
      goToStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > STEPS.BASICS) {
      goToStep(currentStep - 1);
    }
  };

  // Category selection
  const handleCategorySelect = (catId) => {
    setSelectedCategoryId(catId);
    const selectedCat = allCategories.find(c => c.id === catId);
    setCategory(selectedCat);
    setErrors({ ...errors, category: '' });

    // Mark as changed (dirty state for save detection)
    console.log('Category changed to:', selectedCat?.name);
  };

  // Question navigation
  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Question management
  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      type: 'multiple-choice',
      questionText: '',
      imageUrl: '',
      points: 1,
      explanation: '',
      hint: '',
      allowMultipleAnswers: false,
      partialCreditEnabled: false,
      partialCreditMode: 'proportional',
      randomizeOptions: false,
      options: [
        { id: Date.now() + 1, text: '', isCorrect: true },
        { id: Date.now() + 2, text: '', isCorrect: false }
      ],
      correctAnswers: [
        { value: '', isPrimary: true }
      ],
      matchingMode: 'exact-case-insensitive',
      correctAnswer: null
    };
    setQuestions([...questions, newQuestion]);
    setCurrentQuestionIndex(questions.length);
  };

  const deleteCurrentQuestion = () => {
    if (questions.length === 1) {
      return; // Silent fail - just don't delete
    }

    const newQuestions = questions.filter((_, idx) => idx !== currentQuestionIndex);
    setQuestions(newQuestions);

    if (currentQuestionIndex >= newQuestions.length) {
      setCurrentQuestionIndex(newQuestions.length - 1);
    }
  };

  const updateCurrentQuestion = (updates) => {
    setQuestions(questions.map((q, idx) =>
      idx === currentQuestionIndex ? { ...q, ...updates } : q
    ));
  };

  const updateQuestionText = (text) => {
    updateCurrentQuestion({ questionText: text });
  };

  const updateQuestionPoints = (points) => {
    const numPoints = parseInt(points) || 0;
    if (numPoints >= 1 && numPoints <= 10) {
      updateCurrentQuestion({ points: numPoints });
    }
  };

  const updateQuestionExplanation = (explanation) => {
    updateCurrentQuestion({ explanation });
  };

  const updateQuestionHint = (hint) => {
    updateCurrentQuestion({ hint });
  };

  const updateQuestionType = (type) => {
    // const currentQuestion = questions[currentQuestionIndex];

    // Initialize new type with defaults
    const typeDefaults = {
      'multiple-choice': {
        type: 'multiple-choice',
        allowMultipleAnswers: false,
        partialCreditEnabled: false,
        partialCreditMode: 'proportional',
        randomizeOptions: false,
        options: [
          { id: Date.now() + 1, text: '', isCorrect: true },
          { id: Date.now() + 2, text: '', isCorrect: false }
        ]
      },
      'short-answer': {
        type: 'short-answer',
        correctAnswers: [{ value: '', isPrimary: true }],
        matchingMode: 'exact-case-insensitive'
      },
      'true-false': {
        type: 'true-false',
        correctAnswer: null
      }
    };

    // Keep common fields (questionText, points, explanation) and add type-specific defaults
    updateCurrentQuestion({
      ...typeDefaults[type]
    });
  };

  const updateQuestionImage = (url) => {
    updateCurrentQuestion({ imageUrl: url });
  };

  const updateQuestionImageCaption = (caption) => {
    updateCurrentQuestion({ imageCaption: caption });
  };

  const toggleMultipleAnswers = () => {
    const currentQuestion = questions[currentQuestionIndex];
    updateCurrentQuestion({
      allowMultipleAnswers: !currentQuestion.allowMultipleAnswers,
      // Reset to single correct answer when switching to single mode
      options: !currentQuestion.allowMultipleAnswers
        ? currentQuestion.options
        : currentQuestion.options.map((opt, idx) => ({ ...opt, isCorrect: idx === 0 }))
    });
  };

  const addOption = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion.options.length >= 6) {
      return; // Silent fail
    }

    const newOption = { id: Date.now(), text: '', isCorrect: false };
    updateCurrentQuestion({
      options: [...currentQuestion.options, newOption]
    });
  };

  const deleteOption = (optionId) => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion.options.length <= 2) {
      return; // Silent fail
    }

    updateCurrentQuestion({
      options: currentQuestion.options.filter(opt => opt.id !== optionId)
    });
  };

  const updateOptionText = (optionId, text) => {
    const currentQuestion = questions[currentQuestionIndex];
    updateCurrentQuestion({
      options: currentQuestion.options.map(opt =>
        opt.id === optionId ? { ...opt, text } : opt
      )
    });
  };

  const toggleCorrectAnswer = (optionId) => {
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion.allowMultipleAnswers) {
      // Multiple answers: toggle this option
      updateCurrentQuestion({
        options: currentQuestion.options.map(opt =>
          opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
        )
      });
    } else {
      // Single answer: set only this option as correct
      updateCurrentQuestion({
        options: currentQuestion.options.map(opt => ({
          ...opt,
          isCorrect: opt.id === optionId
        }))
      });
    }
  };

  // Validation (returns true if valid, false if invalid)
  const validateQuiz = () => {
    if (!quizTitle.trim()) {
      goToStep(STEPS.BASICS);
      setErrors({ title: 'Quiz title is required' });
      return false;
    }

    if (!selectedCategoryId) {
      goToStep(STEPS.BASICS);
      setErrors({ category: 'Category is required' });
      return false;
    }

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];

      // Common validation
      if (!q.questionText.trim()) {
        setCurrentQuestionIndex(i);
        goToStep(STEPS.QUESTIONS);
        return false;
      }

      if (!q.points || q.points < 1 || q.points > 10) {
        setCurrentQuestionIndex(i);
        goToStep(STEPS.QUESTIONS);
        return false;
      }

      // Type-specific validation
      const questionType = q.type || 'multiple-choice';

      if (questionType === 'multiple-choice') {
        // Multiple Choice validation
        if (q.options.length < 2) {
          setCurrentQuestionIndex(i);
          goToStep(STEPS.QUESTIONS);
          return false;
        }

        const hasCorrectAnswer = q.options.some(opt => opt.isCorrect);
        if (!hasCorrectAnswer) {
          setCurrentQuestionIndex(i);
          goToStep(STEPS.QUESTIONS);
          return false;
        }

        for (let j = 0; j < q.options.length; j++) {
          if (!q.options[j].text.trim()) {
            setCurrentQuestionIndex(i);
            goToStep(STEPS.QUESTIONS);
            return false;
          }
        }
      } else if (questionType === 'short-answer') {
        // Short Answer validation
        const primaryAnswer = q.correctAnswers?.find(a => a.isPrimary);
        if (!primaryAnswer || !primaryAnswer.value.trim()) {
          setCurrentQuestionIndex(i);
          goToStep(STEPS.QUESTIONS);
          return false;
        }
      } else if (questionType === 'true-false') {
        // True/False validation
        if (q.correctAnswer === null || q.correctAnswer === undefined) {
          setCurrentQuestionIndex(i);
          goToStep(STEPS.QUESTIONS);
          return false;
        }
      }
    }

    return true;
  };

  // Save quiz (as draft or published)
  const handleSaveQuiz = async (publish = false) => {
    if (!validateQuiz()) return;

    setLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        notify({ title: 'Error', message: 'You must be logged in', type: 'error' });
        return;
      }

      const quizData = {
        title: quizTitle.trim(),
        description: quizDescription.trim() || `A quiz with ${questions.length} questions worth ${totalPoints} points`,
        image: coverImage.trim() || category?.image || '',
        category: selectedCategoryId,
        categoryName: category?.name || '',
        categorySlug: category?.slug || '',
        // In edit mode, keep current published state; in create mode, use publish param
        isPublic: isEditMode ? isPublic : (publish ? isPublic : false),
        isDraft: isEditMode ? !isPublished : !publish, // Edit mode: keep published status; Create mode: based on publish button
        totalPoints,
        totalQuestions: questions.length,
        settings: {
          timeLimit: hasTimeLimit ? timeLimit : null,
          navigationType,
          reviewPermission,
          hasPassingScore,
          passingScore,
          isPublic: isEditMode ? isPublic : (publish ? isPublic : false),
          shuffleQuestions,
          attemptLimit,
          maxAttempts: attemptLimit === 'custom' ? maxAttempts : null
        },
        questions: questions.map(q => {
          const baseQuestion = {
            type: q.type || 'multiple-choice',
            questionText: q.questionText.trim(),
            imageUrl: q.imageUrl.trim() || null,
            imageCaption: q.imageCaption?.trim() || null,
            points: parseInt(q.points) || 1,
            explanation: q.explanation?.trim() || null,
            hint: q.hint?.trim() || null
          };

          // Add type-specific fields
          if (baseQuestion.type === 'multiple-choice') {
            return {
              ...baseQuestion,
              allowMultipleAnswers: q.allowMultipleAnswers,
              partialCreditEnabled: q.partialCreditEnabled || false,
              partialCreditMode: q.partialCreditMode || 'proportional',
              randomizeOptions: randomizeOptions, // Global setting from Step 3
              options: q.options.map(opt => ({
                text: opt.text.trim(),
                isCorrect: opt.isCorrect
              }))
            };
          } else if (baseQuestion.type === 'short-answer') {
            return {
              ...baseQuestion,
              correctAnswers: q.correctAnswers.filter(a => a.value.trim()).map(a => ({
                value: a.value.trim(),
                isPrimary: a.isPrimary
              })),
              matchingMode: q.matchingMode
            };
          } else if (baseQuestion.type === 'true-false') {
            return {
              ...baseQuestion,
              correctAnswer: q.correctAnswer
            };
          }

          return baseQuestion;
        }),
        createdBy: userId
      };

      let result;
      if (isEditMode && quizId) {
        result = await updateQuiz(quizId, quizData);
      } else {
        result = await createQuiz(quizData);
      }

      if (result.success) {
        // Clear navigation block since quiz is saved
        setNavigationBlock(false);

        // Close modal immediately and clean URL
        if (isModalMode) {
          onClose();
        }

        // Navigate back to category if available, otherwise to main dashboard
        const targetRoute = category
          ? `/category/${category.slug || category.id}`
          : '/main';

        navigate(targetRoute, { replace: true });

        // Show success notification after navigation
        setTimeout(() => {
          const categoryContext = category ? ` to ${category.name}` : '';
          notify({
            title: isEditMode ? 'Quiz Updated Successfully!' : 'Quiz Created Successfully!',
            message: `"${quizTitle}" has been ${isEditMode ? 'updated' : 'added'}${categoryContext}`,
            type: 'success',
            showInCenter: true
          });
        }, 100);
      } else {
        // Handle duplicate quiz name error specially
        if (result.isDuplicate) {
          notify({
            title: 'Duplicate Quiz Name',
            message: result.error || 'A quiz with this name already exists in the selected category',
            type: 'error',
            duration: 6000
          });

          // Highlight the category field to help user
          setErrors({ category: 'Quiz name already exists in this category' });
          goToStep(STEPS.BASICS);
        } else {
          notify({
            title: 'Error',
            message: result.error || 'Failed to save quiz',
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      notify({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  // Helper function to jump to specific question by ID
  const goToQuestionById = (questionId) => {
    const idx = questions.findIndex(q => q.id === questionId);
    if (idx !== -1) {
      setCurrentQuestionIndex(idx);
    }
  };

  // Modal mode rendering
  if (isModalMode && !isOpen) return null;

  // Loading skeleton for data fetching
  const loadingContent = (
    <div className="flex-1 overflow-hidden">
      <div className="max-w-6xl mx-auto p-6 animate-pulse">
        {/* Header skeleton */}
        <div className="mb-8">
          <div className="h-8 bg-slate-200 rounded-lg w-48 mb-4"></div>
          <div className="flex gap-4">
            <div className="h-12 w-12 bg-slate-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-slate-200 rounded w-32 mb-2"></div>
              <div className="h-3 bg-slate-200 rounded w-48"></div>
            </div>
          </div>
        </div>

        {/* Progress bar skeleton */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-12 w-12 bg-slate-200 rounded-full"></div>
          <div className="h-1 flex-1 bg-slate-200 rounded"></div>
          <div className="h-12 w-12 bg-slate-200 rounded-full"></div>
          <div className="h-1 flex-1 bg-slate-200 rounded"></div>
          <div className="h-12 w-12 bg-slate-200 rounded-full"></div>
        </div>

        {/* Form fields skeleton */}
        <div className="space-y-6">
          <div>
            <div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
            <div className="h-12 bg-slate-200 rounded-lg"></div>
          </div>
          <div>
            <div className="h-4 bg-slate-200 rounded w-32 mb-2"></div>
            <div className="h-32 bg-slate-200 rounded-lg"></div>
          </div>
          <div>
            <div className="h-4 bg-slate-200 rounded w-28 mb-2"></div>
            <div className="h-12 bg-slate-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const wizardContent = (
    <div className={`flex-1 ${isModalMode ? 'overflow-y-auto popup-scrollable-workspace' : 'px-6 pb-4 overflow-y-auto popup-scrollable-workspace'}`} style={isModalMode ? { overflowX: 'hidden' } : {}}>
      <div className={isModalMode ? "h-full" : "max-w-6xl mx-auto"}>
            {/* Step Content */}
            <div className="transition-all duration-300">
              {currentStep === STEPS.BASICS && (
                <BasicsStep
                  categories={allCategories}
                  selectedCategoryId={selectedCategoryId}
                  onSelectCategory={handleCategorySelect}
                  title={quizTitle}
                  setTitle={setQuizTitle}
                  description={quizDescription}
                  setDescription={setQuizDescription}
                  coverImage={coverImage}
                  setCoverImage={setCoverImage}
                  hasTimeLimit={hasTimeLimit}
                  setHasTimeLimit={setHasTimeLimit}
                  timeLimit={timeLimit}
                  setTimeLimit={setTimeLimit}
                  navigationType={navigationType}
                  setNavigationType={setNavigationType}
                  reviewPermission={reviewPermission}
                  setReviewPermission={setReviewPermission}
                  attemptLimit={attemptLimit}
                  setAttemptLimit={setAttemptLimit}
                  maxAttempts={maxAttempts}
                  setMaxAttempts={setMaxAttempts}
                  errors={errors}
                  setErrors={setErrors}
                  onNext={goToNextStep}
                  onCancel={() => {
                    // Clear navigation block
                    setNavigationBlock(false);

                    if (isModalMode) {
                      onClose();
                    } else {
                      // Always go to main dashboard (security)
                      navigate('/main', { replace: true });
                    }
                  }}
                  currentStep={currentStep}
                  category={category}
                  navigate={navigate}
                  badgeState={getBadgeState()}
                  isModalMode={isModalMode}
                />
              )}
              {currentStep === STEPS.QUESTIONS && (
                <QuestionsStep
                  question={currentQuestion}
                  questionNumber={currentQuestionIndex + 1}
                  totalQuestions={questions.length}
                  totalPoints={totalPoints}
                  onUpdateText={updateQuestionText}
                  onUpdatePoints={updateQuestionPoints}
                  onUpdateImage={updateQuestionImage}
                  onUpdateImageCaption={updateQuestionImageCaption}
                  onUpdateExplanation={updateQuestionExplanation}
                  onUpdateHint={updateQuestionHint}
                  onUpdateType={updateQuestionType}
                  onUpdateQuestion={updateCurrentQuestion}
                  onToggleMultipleAnswers={toggleMultipleAnswers}
                  onAddOption={addOption}
                  onDeleteOption={deleteOption}
                  onUpdateOption={updateOptionText}
                  onToggleCorrect={toggleCorrectAnswer}
                  onDelete={deleteCurrentQuestion}
                  canDelete={questions.length > 1}
                  onPrevious={goToPreviousQuestion}
                  onNext={goToNextQuestion}
                  onAddQuestion={addQuestion}
                  questions={questions}
                  currentIndex={currentQuestionIndex}
                  setCurrentIndex={setCurrentQuestionIndex}
                  onPreviousStep={goToPreviousStep}
                  onNextStep={goToNextStep}
                  notify={notify}
                />
              )}
              {currentStep === STEPS.REVIEW && (
                <ReviewStep
                  quiz={{
                    title: quizTitle,
                    description: quizDescription,
                    category: category?.name,
                    totalQuestions: questions.length,
                    totalPoints,
                    settings: {
                      hasTimeLimit,
                      timeLimit,
                      navigationType,
                      reviewPermission,
                      isPublic,
                      shuffleQuestions,
                      randomizeOptions,
                      hasPassingScore,
                      passingScore
                    },
                    questions
                  }}
                  isPublic={isPublic}
                  setIsPublic={setIsPublic}
                  shuffleQuestions={shuffleQuestions}
                  setShuffleQuestions={setShuffleQuestions}
                  randomizeOptions={randomizeOptions}
                  setRandomizeOptions={setRandomizeOptions}
                  hasTimeLimit={hasTimeLimit}
                  setHasTimeLimit={setHasTimeLimit}
                  timeLimit={timeLimit}
                  setTimeLimit={setTimeLimit}
                  navigationType={navigationType}
                  setNavigationType={setNavigationType}
                  reviewPermission={reviewPermission}
                  setReviewPermission={setReviewPermission}
                  attemptLimit={attemptLimit}
                  setAttemptLimit={setAttemptLimit}
                  maxAttempts={maxAttempts}
                  setMaxAttempts={setMaxAttempts}
                  hasPassingScore={hasPassingScore}
                  setHasPassingScore={setHasPassingScore}
                  passingScore={passingScore}
                  setPassingScore={setPassingScore}
                  onPreviousStep={goToPreviousStep}
                  badgeState={getBadgeState()}
                  onPublish={() => handleSaveQuiz(true)}
                  loading={loading}
                  isEditMode={isEditMode}
                />
              )}
            </div>

            {/* Navigation Footer - Now integrated inside ReviewStep component */}
            {false && currentStep === STEPS.REVIEW && (
              <div className="sticky bottom-0 bg-white border-t-2 border-[#E2E8F0] py-4 px-6 rounded-t-xl shadow-lg mt-6">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveQuiz(false)}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        <span>Save as Draft</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveQuiz(true)}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Publishing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Create</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
  );

  // Modal mode wrapper
  if (isModalMode) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <div
          className="w-full bg-white border border-[#E2E8F0] rounded-2xl shadow-2xl flex flex-col"
          style={{
            maxWidth: '900px',
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden'
          }}
        >
          {dataLoading ? loadingContent : wizardContent}

          {/* Validation Modal */}
          {!dataLoading && (
            <ValidationModal
              show={showValidationModal}
              errors={validationErrors}
              onClose={() => setShowValidationModal(false)}
              onGoToQuestion={goToQuestionById}
            />
          )}
        </div>
      </div>
    );
  }

  // Page mode - full layout with Navbar and Header
  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC] max-w-full">
      <Navbar />

      <main className="flex-1 flex flex-col overflow-hidden max-w-full">
        <div className="flex-shrink-0 py-4 md:py-6 px-4 md:px-8 bg-[#F8FAFC]">
          <DashboardHeader
            toggleDropdown={toggleDropdown}
            showDropdown={showDropdown}
            Avatar={Avatar}
          />
        </div>

        {dataLoading ? loadingContent : wizardContent}
      </main>

      {/* Validation Modal */}
      {!dataLoading && (
        <ValidationModal
          show={showValidationModal}
          errors={validationErrors}
          onClose={() => setShowValidationModal(false)}
          onGoToQuestion={goToQuestionById}
        />
      )}

      {/* Loading Overlay - Shows during quiz creation/update */}
      {loading && (
        <LoadingOverlay
          message={isEditMode ? "Updating Quiz..." : "Creating Your Quiz..."}
          subMessage={isEditMode ? "Saving your changes" : "Setting up your quiz with all the details"}
        />
      )}
    </div>
  );
}

// STEP 1: Quiz Basics - Premium, searchable, with tactile privacy cards
const BasicsStep = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  title,
  setTitle,
  description,
  setDescription,
  coverImage,
  setCoverImage,
  hasTimeLimit,
  setHasTimeLimit,
  timeLimit,
  setTimeLimit,
  navigationType,
  setNavigationType,
  reviewPermission,
  setReviewPermission,
  attemptLimit,
  setAttemptLimit,
  maxAttempts,
  setMaxAttempts,
  errors,
  setErrors,
  onNext,
  onCancel,
  currentStep,
  category,
  navigate,
  badgeState,
  isModalMode = false
}) => {
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [imageUploadError, setImageUploadError] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState(''); // Track uploaded file name
  const [showExitModal, setShowExitModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });
  const categoryTriggerRef = useRef(null);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  // Smart dropdown position - detects space and opens up/down accordingly
  const updateDropdownPosition = () => {
    if (categoryTriggerRef.current) {
      const rect = categoryTriggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 190; // Compact: search (50px) + 2 items (130px) + padding (10px)

      // Calculate available space below and above
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide if we should open upward or downward
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Position dropdown flush with button for seamless connection
      const offset = 0; // No gap - seamless extension

      setDropdownPosition({
        top: shouldOpenUpward ? rect.top - dropdownHeight - 4 : rect.bottom + offset,
        left: rect.left,
        width: rect.width,
        openUpward: shouldOpenUpward
      });
    }
  };

  // Handle dropdown toggle
  const handleCategoryToggle = () => {
    if (!showCategoryDropdown) {
      // Force immediate position calculation
      if (categoryTriggerRef.current) {
        const rect = categoryTriggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    }
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  // Update position immediately when dropdown opens (useLayoutEffect runs synchronously before paint)
  React.useLayoutEffect(() => {
    if (showCategoryDropdown) {
      updateDropdownPosition();
      // Add a small delay to trigger animation
      const dropdownEl = document.getElementById('category-dropdown-portal-basics');
      if (dropdownEl) {
        dropdownEl.style.opacity = '0';
        dropdownEl.style.transform = 'translateY(-5px)';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            dropdownEl.style.opacity = '1';
            dropdownEl.style.transform = 'translateY(0)';
          });
        });
      }
    }
  }, [showCategoryDropdown]);

  // Continuously update position while dropdown is open
  useEffect(() => {
    if (!showCategoryDropdown) return;

    const handleUpdatePosition = () => {
      updateDropdownPosition();
    };

    // Update on any scroll event (including inside modal)
    const scrollableElements = document.querySelectorAll('.popup-scrollable-workspace');
    scrollableElements.forEach(el => {
      el.addEventListener('scroll', handleUpdatePosition);
    });

    return () => {
      scrollableElements.forEach(el => {
        el.removeEventListener('scroll', handleUpdatePosition);
      });
    };
  }, [showCategoryDropdown]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryTriggerRef.current && !categoryTriggerRef.current.contains(e.target)) {
        const dropdown = document.getElementById('category-dropdown-portal-basics');
        if (dropdown && !dropdown.contains(e.target)) {
          setShowCategoryDropdown(false);
        }
      }
    };

    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showCategoryDropdown]);

  // Filter categories based on search
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearchQuery.toLowerCase())
  );

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return title.trim() !== '' || description.trim() !== '' || coverImage !== '' || selectedCategoryId !== '';
  };

  // Handle back navigation with confirmation
  const handleBackClick = () => {
    if (hasUnsavedChanges()) {
      setShowExitModal(true);
    } else {
      if (category) {
        navigate(`/category/${category.slug || category.id}`);
      } else {
        navigate('/main');
      }
    }
  };

  // Confirm exit and discard changes
  const confirmExit = () => {
    setShowExitModal(false);
    if (category) {
      navigate(`/category/${category.slug || category.id}`);
    } else {
      navigate('/main');
    }
  };

  // Validation with visual feedback
  const handleNext = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Quiz title is required';
    }

    if (!description.trim()) {
      newErrors.description = 'Summary and instructions are required';
    }

    if (!selectedCategoryId) {
      newErrors.category = 'Please select a category';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onNext();
  };

  return (
    <div className={isModalMode ? "h-full flex flex-col" : "w-full max-w-[1120px] mx-auto"}>
      {/* Main Card - Everything Inside */}
      <div className={isModalMode ? "h-full flex flex-col bg-white rounded-2xl border border-[#E2E8F0]" : "bg-white rounded-2xl shadow-sm border border-[#E2E8F0]"} style={{ overflow: isModalMode ? 'hidden' : 'visible', height: isModalMode ? '100%' : 'auto' }}>
        {/* Header Section Inside Card */}
        <div className={isModalMode ? "px-8 pt-6 pb-0 flex-shrink-0" : "px-10 pt-10 pb-0"}>
          {/* Top Header Row - Perfect Balance */}
          <div className="flex items-center justify-between w-full mb-4">
            {/* Left: Back Button + Title with Green Dot */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackClick}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5 text-[#64748B]" />
              </button>
              <h2 className="text-xl font-semibold text-[#0F172A] tracking-tight flex items-center gap-2">
                <span>Quiz Configuration</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" style={{ marginLeft: '4px' }}></span>
              </h2>
            </div>

            {/* Right: Status Badge - Dynamic */}
            <div className="px-3 py-1.5 rounded-full text-[13px] font-semibold uppercase tracking-wide border flex items-center gap-1.5"
              style={{
                backgroundColor: badgeState.bg,
                borderColor: badgeState.border,
                color: badgeState.color
              }}
            >
              <span>{badgeState.icon}</span>
              <span>{badgeState.text}</span>
            </div>
          </div>

          {/* Subtle Border Below Header */}
          <div className="w-full h-px bg-[#E2E8F0] mb-4"></div>

          {/* Wizard Stepper - Isolated with Breathing Room */}
          <div className={isModalMode ? "py-4 mb-4" : "py-6 mb-7"}>
            <div className="flex items-start justify-center gap-4">
              {[
                { num: 1, label: '1. Quiz Basics' },
                { num: 2, label: '2. Building Questions' },
                { num: 3, label: '3. Configure & Publish' }
              ].map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-col items-center flex-1 max-w-[140px]">
                    <div
                      className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                        idx === currentStep
                          ? 'bg-[#10B981] text-white shadow-md shadow-emerald-200'
                          : idx < currentStep
                          ? 'bg-[#10B981] text-white'
                          : 'bg-[#94A3B8] text-white'
                      }`}
                    >
                      {idx < currentStep ? '✓' : step.num}
                    </div>
                    {/* Always show label for all steps */}
                    <span
                      className={`text-[13px] mt-2 text-center leading-tight transition-colors ${
                        idx === currentStep
                          ? 'text-[#0F172A] font-semibold'
                          : 'text-[#94A3B8] font-normal'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className="relative flex-shrink-0" style={{ width: '60px', height: '32px' }}>
                      {/* Perfectly Centered Line - Top 50% of circle height */}
                      <div
                        className={`absolute h-0.5 transition-all ${
                          idx < currentStep
                            ? 'bg-[#10B981]'
                            : idx === currentStep
                            ? 'bg-[#A7F3D0]'
                            : 'bg-[#E2E8F0]'
                        }`}
                        style={{
                          top: '16px', // 50% of 32px circle height
                          left: 0,
                          right: 0
                        }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Clean Divider Below Stepper */}
            <div className="w-full h-px bg-[#E2E8F0] mt-5"></div>
          </div>
        </div>

        {/* Form Fields Section */}
        <div className={isModalMode ? "px-8 pt-2 pb-4 space-y-4 flex-1 overflow-visible" : "px-10 pt-6 pb-10 space-y-6"}>
          {/* Quiz Title */}
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] mb-2">
              Quiz Title <span className="text-[#EF4444]">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors({ ...errors, title: '' });
              }}
              placeholder="e.g., Cardiology Essentials"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none text-[#0F172A] placeholder-[#94A3B8] transition-all ${
                errors.title
                  ? 'border-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.1)]'
                  : 'border-[#E2E8F0] focus:border-[#10B981] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)]'
              }`}
            />
            {errors.title && (
              <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.title}</p>
            )}
          </div>

          {/* Summary & Instructions */}
          <div>
            <label className="block text-sm font-semibold text-[#0F172A] mb-2">
              Summary & Instructions <span className="text-[#EF4444]">*</span>
            </label>
            <div className="relative">
              <textarea
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 120) {
                    setDescription(e.target.value);
                    setErrors({ ...errors, description: '' });
                  }
                }}
                placeholder="Provide clear instructions and context for quiz takers..."
                rows={3}
                maxLength={120}
                style={{ minHeight: '100px', paddingBottom: '32px' }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none text-[#0F172A] placeholder-[#94A3B8] resize-none transition-all ${
                  errors.description
                    ? 'border-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.1)]'
                    : 'border-[#E2E8F0] focus:border-[#10B981] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)]'
                }`}
              />
              {/* Character Counter Inside Textarea */}
              <div className="absolute bottom-3 right-4 left-4 flex justify-between items-center pointer-events-none">
                <span className="text-xs text-[#94A3B8]">Keep it concise and clear</span>
                <span className={`text-xs font-medium ${description.length >= 120 ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`}>
                  {description.length}/120
                </span>
              </div>
            </div>
            {errors.description && (
              <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.description}</p>
            )}
          </div>

          {/* Cover Image - Inline Single Row */}
          <div>
            <div className="flex flex-col gap-1 mb-2">
              <label className="text-sm font-semibold text-[#0F172A]">
                Cover Image <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <p className="text-xs text-gray-600">
                Add a cover image to make your quiz more attractive. Upload a file or paste an image URL.
              </p>
            </div>

            {/* Single Row: Input + Upload Button - Exact 44px Height */}
            <div className="flex items-stretch gap-3">
              <input
                type="text"
                value={uploadedFileName || (coverImage && !coverImage.startsWith('data:') ? coverImage : '')}
                onChange={(e) => {
                  const value = e.target.value.trim();

                  // Clear uploaded file name when user types
                  setUploadedFileName('');

                  // Security: Sanitize and validate URL
                  if (value) {
                    try {
                      // Only allow http:// and https:// protocols (blocks javascript:, data:, file:, etc.)
                      const url = new URL(value);
                      if (url.protocol === 'http:' || url.protocol === 'https:') {
                        // Valid HTTP/HTTPS URL - accept it
                        setCoverImage(value);
                        setImageUploadError('');
                      } else {
                        setImageUploadError('Only HTTP/HTTPS URLs are allowed');
                      }
                    } catch (err) {
                      // Invalid URL format - allow typing but show as value
                      setCoverImage(value);
                      setImageUploadError('');
                    }
                  } else {
                    setCoverImage('');
                    setImageUploadError('');
                  }
                }}
                placeholder={uploadedFileName ? "File uploaded" : "Paste image address (optional)"}
                disabled={!!uploadedFileName}
                style={{ height: '44px' }}
                className={`flex-1 px-4 border border-[#E2E8F0] rounded-lg focus:outline-none text-[14px] text-[#0F172A] placeholder-[#94A3B8] transition-all focus:border-[#10B981] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)] box-border ${uploadedFileName ? 'bg-[#F8FAFC] cursor-not-allowed' : ''}`}
              />
              <label className="cursor-pointer flex-shrink-0">
                <div
                  style={{ height: '44px' }}
                  className="px-4 py-0 border border-[#E2E8F0] bg-white rounded-lg hover:border-[#10B981] hover:bg-[#F0FDF4]/30 transition-all flex items-center gap-2 box-border"
                >
                  <svg className="w-5 h-5 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-[14px] font-medium text-[#0F172A]">Upload</span>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Security: Validate file type
                      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                      if (!validTypes.includes(file.type)) {
                        setImageUploadError('Only JPG, PNG, GIF, and WebP images are allowed');
                        return;
                      }

                      // Check file size (500KB = 512000 bytes)
                      if (file.size > 512000) {
                        setImageUploadError('Image size must be less than 500KB');
                        return;
                      }

                      // Validate file name for security (prevent directory traversal)
                      const fileName = file.name;
                      if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
                        setImageUploadError('Invalid file name');
                        return;
                      }

                      setImageUploadError('');

                      // Convert to data URL for preview
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        // Security: Verify result is actually a data URL
                        const result = reader.result;
                        if (typeof result === 'string' && result.startsWith('data:image/')) {
                          setCoverImage(result);
                          setUploadedFileName(fileName); // Show file name instead of data URL
                        } else {
                          setImageUploadError('Invalid image file');
                        }
                      };
                      reader.onerror = () => {
                        setImageUploadError('Failed to read file');
                      };
                      reader.readAsDataURL(file);
                    }
                    // Clear file input to allow re-uploading same file
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {/* Error Message */}
            {imageUploadError && (
              <p className="text-xs text-red-600 font-medium mt-2">{imageUploadError}</p>
            )}

            {/* Preview - Matches card aspect ratio (5:2) */}
            {coverImage && !imageUploadError && (
              <div className="relative rounded-lg overflow-hidden border border-[#E2E8F0] mt-3">
                <img
                  src={coverImage}
                  alt="Quiz cover preview"
                  className="w-full object-cover"
                  style={{ aspectRatio: '5/2' }}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    // Only show placeholder, don't set error (user can still proceed)
                    e.target.src = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=800';
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setCoverImage('');
                    setUploadedFileName('');
                    setImageUploadError('');
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-lg"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Category - Searchable Dropdown */}
          <div className="relative" style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <label className="block text-sm font-semibold text-[#0F172A] mb-2">
              Category <span className="text-[#EF4444]">*</span>
            </label>
            <button
              ref={categoryTriggerRef}
              type="button"
              onClick={() => {
                handleCategoryToggle();
                setCategorySearchQuery('');
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none text-left flex items-center gap-3 bg-white transition-all duration-200 ${
                errors.category
                  ? 'border-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.1)]'
                  : showCategoryDropdown
                  ? 'border-[#10B981] shadow-[0_0_0_4px_rgba(16,185,129,0.1)]'
                  : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
              }`}
            >
              {selectedCategory ? (
                <>
                  <img
                    src={selectedCategory.image}
                    alt={selectedCategory.name}
                    className="w-8 h-8 rounded-lg object-cover border border-[#E2E8F0] flex-shrink-0"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=100';
                    }}
                  />
                  <span className="text-[#0F172A] font-medium flex-1">{selectedCategory.name}</span>
                </>
              ) : (
                <span className="text-[#94A3B8] flex-1">Select a category...</span>
              )}
              <svg
                className={`w-5 h-5 text-[#64748B] transition-transform duration-200 flex-shrink-0 ${showCategoryDropdown ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {errors.category && (
              <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.category}</p>
            )}

            {showCategoryDropdown && createPortal(
              <div
                id="category-dropdown-portal-basics"
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  zIndex: 9999,
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                  border: '1px solid #10B981',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'opacity 200ms ease-out, transform 200ms ease-out'
                }}
              >
                {/* Search Box - Compact */}
                <div style={{ padding: '10px 12px' }} className="border-b border-[#E2E8F0] bg-gradient-to-b from-[#F8FAFC] to-white">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={categorySearchQuery}
                      onChange={(e) => setCategorySearchQuery(e.target.value)}
                      placeholder="Search categories..."
                      className="w-full pl-10 pr-4 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none placeholder-[#94A3B8] bg-white transition-all focus:border-[#10B981] focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)]"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Category List - Compact: Shows 2 items */}
                <div
                  className="custom-scrollbar"
                  style={{
                    maxHeight: '130px',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    padding: '6px'
                  }}
                >
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          onSelectCategory(cat.id);
                          setShowCategoryDropdown(false);
                          setCategorySearchQuery('');
                          setErrors({ ...errors, category: '' });
                        }}
                        className={`w-full text-left flex items-center gap-3 cursor-pointer transition-all rounded-lg ${
                          selectedCategoryId === cat.id
                            ? 'bg-[#F0FDF4] border border-[#10B981]/20'
                            : 'border border-transparent hover:bg-[#F8FAFC] hover:border-[#E2E8F0]'
                        }`}
                        style={{
                          padding: '10px 12px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#0F172A'
                        }}
                      >
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-9 h-9 rounded-lg object-cover border border-[#E2E8F0] flex-shrink-0 shadow-sm"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=100';
                          }}
                        />
                        <span className="flex-1 text-sm font-medium">{cat.name}</span>
                        {selectedCategoryId === cat.id && (
                          <svg className="w-5 h-5 text-[#10B981] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-12 text-center">
                      <svg className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-[#64748B] font-medium">No categories found</p>
                      <p className="text-xs text-[#94A3B8] mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>

              </div>,
              document.body
            )}
          </div>
        </div>

        {/* Action Footer Bar - White Canvas with Border */}
        <div className={isModalMode ? "px-8 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-between gap-4 flex-shrink-0" : "px-10 py-6 border-t border-[#E2E8F0] bg-white flex items-center justify-between gap-4"}>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 border border-[#E2E8F0] rounded-lg text-[#64748B] font-medium transition-all duration-200 hover:border-[#FCA5A5] hover:text-[#EF4444] hover:bg-[#FEF2F2]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="px-6 py-2.5 bg-[#10B981] hover:bg-[#0D9488] text-white font-semibold rounded-lg transition-all flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-[#E2E8F0]">
              <h3 className="text-xl font-bold text-[#0F172A]">Discard Changes?</h3>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-[#64748B] leading-relaxed">
                You have unsaved changes in your quiz. If you leave now, all your progress will be lost.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center justify-end gap-3">
              <button
                onClick={() => setShowExitModal(false)}
                className="px-5 py-2.5 border border-[#E2E8F0] rounded-lg text-[#64748B] font-medium hover:bg-white transition-all"
              >
                Keep Editing
              </button>
              <button
                onClick={confirmExit}
                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tooltip Component with smart positioning
const Tooltip = ({ text, side = 'right' }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Check if tooltip would overflow on right side
      const useLeftSide = side === 'left' || (rect.right + 280 > viewportWidth);

      setPosition({
        top: rect.top + window.scrollY,
        left: useLeftSide ? rect.left + window.scrollX - 272 : rect.right + window.scrollX + 8,
        useLeftSide
      });
    }
    setShow(true);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors ml-1.5"
        aria-label="More information"
      >
        <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {show && (
        <div
          className="fixed z-[100] w-64 px-3 py-2 bg-[#1E293B] text-white text-xs leading-relaxed rounded-md shadow-xl pointer-events-none"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            opacity: 1,
            transition: 'opacity 150ms ease-out'
          }}
        >
          {text}
          <div
            className={`absolute top-1.5 w-2 h-2 bg-[#1E293B] transform rotate-45 ${
              position.useLeftSide ? 'right-[-4px]' : 'left-[-4px]'
            }`}
          ></div>
        </div>
      )}
    </div>
  );
};

// Helper function to get type label (NO EMOJIS)
const getTypeLabel = (question) => {
  const type = question.type || 'multiple-choice';

  if (type === 'multiple-choice') {
    return question.allowMultipleAnswers ? 'Multiple choice' : 'Single choice';
  } else if (type === 'short-answer') {
    return 'Short answer';
  } else if (type === 'true-false') {
    return 'True or false';
  }

  return 'Single choice';
};

// Helper function to check if question has content
const checkQuestionHasContent = (question) => {
  if (question.questionText.trim()) return true;

  const type = question.type || 'multiple-choice';

  if (type === 'multiple-choice') {
    return question.options?.some(opt => opt.text.trim());
  } else if (type === 'short-answer') {
    return question.correctAnswers?.some(a => a.value.trim());
  } else if (type === 'true-false') {
    return question.correctAnswer !== null;
  }

  return false;
};

// Validation Modal Component
const ValidationModal = ({ show, errors, onClose, onGoToQuestion }) => {
  if (!show || !errors || errors.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#E2E8F0] transform transition-all">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0]">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0F172A]">Questions Need Attention</h3>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-[#64748B] mb-4">
              Some questions are incomplete. Please fix the following issues before proceeding:
            </p>

            <div className="space-y-4">
              {errors.map((error, idx) => (
                <div key={idx} className="p-4 bg-amber-50/50 border border-amber-200 rounded-lg">
                  <button
                    onClick={() => {
                      onGoToQuestion(error.questionId);
                      onClose();
                    }}
                    className="flex items-center gap-2 font-semibold text-[#0F172A] text-sm hover:text-[#10B981] transition-colors mb-2"
                  >
                    <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Question {error.questionNumber}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <ul className="space-y-1">
                    {error.errors.map((err, errIdx) => (
                      <li key={errIdx} className="flex items-start gap-2 text-xs text-[#64748B]">
                        <span className="text-amber-500 flex-shrink-0 mt-0.5">•</span>
                        <span>{err}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 border border-[#E2E8F0] rounded-lg text-[#64748B] font-medium hover:bg-[#F8FAFC] transition-all text-sm"
            >
              Stay & Review
            </button>
            <button
              onClick={() => {
                if (errors.length > 0) {
                  onGoToQuestion(errors[0].questionId);
                  onClose();
                }
              }}
              className="px-4 py-2.5 bg-[#10B981] hover:bg-[#0D9488] text-white font-semibold rounded-lg transition-all shadow-sm text-sm"
            >
              Go to Question {errors[0]?.questionNumber || 1}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// STEP 2: Questions (with polymorphic question types)
const QuestionsStep = ({
  question,
  questionNumber,
  totalQuestions,
  totalPoints,
  onUpdateText,
  onUpdatePoints,
  onUpdateImage,
  onUpdateImageCaption,
  onUpdateExplanation,
  onUpdateHint,
  onUpdateType,
  onUpdateQuestion,
  onToggleMultipleAnswers,
  onAddOption,
  onDeleteOption,
  onUpdateOption,
  onToggleCorrect,
  onDelete,
  canDelete,
  onPrevious,
  onNext,
  onAddQuestion,
  questions,
  currentIndex,
  setCurrentIndex,
  onPreviousStep,
  onNextStep,
  notify
}) => {
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const typeDropdownRef = useRef(null);
  const [showTypeSwitchModal, setShowTypeSwitchModal] = useState(false);
  const [pendingType, setPendingType] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('content'); // 'content' | 'settings' | 'media'

  // Detect scroll for sticky shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
    };

    if (showTypeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTypeDropdown]);

  const handleTypeChange = (newType) => {
    if (newType === question.type) {
      setShowTypeDropdown(false);
      return;
    }

    const hasContent = checkQuestionHasContent(question);

    if (hasContent) {
      setPendingType(newType);
      setShowTypeSwitchModal(true);
      setShowTypeDropdown(false);
    } else {
      onUpdateType(newType);
      setShowTypeDropdown(false);
    }
  };

  const confirmTypeSwitch = () => {
    onUpdateType(pendingType);
    setShowTypeSwitchModal(false);
    setPendingType(null);
  };

  // Get badge state
  const badgeState = {
    icon: '✏️',
    text: 'Draft',
    bg: 'rgba(254, 243, 199, 0.6)',
    border: 'rgba(217, 119, 6, 0.15)',
    color: '#D97706'
  };

  return (
    <div className="w-full max-w-[1120px] mx-auto">
      {/* Global Top Shell - Back + Title + Badge */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] mb-4">
        <div className="px-10 py-4">
          <div className="flex items-center justify-between w-full">
            {/* Left: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={onPreviousStep}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#64748B]" />
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-[#0F172A] tracking-tight">
                  Content Building
                </h2>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
              </div>
            </div>

            {/* Right: Draft Badge */}
            <div
              className="px-3 py-1.5 rounded-full text-[13px] font-semibold uppercase tracking-wide border flex items-center gap-1.5"
              style={{
                backgroundColor: badgeState.bg,
                borderColor: badgeState.border,
                color: badgeState.color
              }}
            >
              <span>{badgeState.icon}</span>
              <span>{badgeState.text}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Wizard Stepper - Inside Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] mb-4">
        <div className="px-10 pt-6 pb-0">
          <div className="py-6 mb-0">
            <div className="flex items-start justify-center gap-4">
              {[
                { num: 1, label: '1. Quiz Basics' },
                { num: 2, label: '2. Building Questions' },
                { num: 3, label: '3. Configure & Publish' }
              ].map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-col items-center flex-1 max-w-[140px]">
                    <div
                      className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                        idx === 1
                          ? 'bg-[#10B981] text-white shadow-md shadow-emerald-200'
                          : idx < 1
                          ? 'bg-[#10B981] text-white'
                          : 'bg-[#94A3B8] text-white'
                      }`}
                    >
                      {idx < 1 ? '✓' : step.num}
                    </div>
                    <span
                      className={`text-[13px] mt-2 text-center leading-tight transition-colors ${
                        idx === 1
                          ? 'text-[#0F172A] font-semibold'
                          : 'text-[#94A3B8] font-normal'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className="relative flex-shrink-0" style={{ width: '60px', height: '32px' }}>
                      <div
                        className={`absolute h-0.5 transition-all ${
                          idx < 1
                            ? 'bg-[#10B981]'
                            : idx === 1
                            ? 'bg-[#A7F3D0]'
                            : 'bg-[#E2E8F0]'
                        }`}
                        style={{
                          top: '16px',
                          left: 0,
                          right: 0
                        }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="w-full h-px bg-[#E2E8F0] mt-6"></div>
          </div>
        </div>
      </div>

      {/* Unified Sticky Question Navigator Bar */}
      <div className={`sticky top-0 z-40 bg-[#F8FAFC] pb-3 transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`}>
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <div className="flex items-start justify-between px-6 py-3 gap-4">
            {/* Left: Question Pills + Add Button (Wrap to multiple lines) */}
            <div className="flex-1 flex flex-wrap items-center gap-2">
              {questions.map((q, idx) => (
                <button
                  key={q.id}
                  onClick={() => setCurrentIndex(idx)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    idx === currentIndex
                      ? 'bg-[#10B981] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Q{idx + 1}
                </button>
              ))}
              <button
                onClick={onAddQuestion}
                className="px-4 py-2 rounded-lg border-2 border-dashed border-[#E2E8F0] text-gray-600 hover:border-[#10B981] hover:text-[#10B981] font-medium text-sm transition-all flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>

            {/* Right: Delete Question Button (Icon Only) */}
            {canDelete && (
              <button
                onClick={onDelete}
                title="Delete question"
                className="flex-shrink-0 p-2 text-[#64748B] hover:text-[#EF4444] hover:bg-red-50 rounded-lg transition-all"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Question Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] overflow-hidden">

        {/* Question Context Header */}
        <div className="px-10 pt-6 pb-4 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-[#0F172A]">
              Question {questionNumber} of {totalQuestions}
            </h2>
            <div className="flex items-center gap-2 bg-[#F8FAFC] px-3 py-1.5 rounded-lg border border-[#E2E8F0]">
              <Star className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm font-semibold text-gray-700">{totalPoints} pts total</span>
            </div>
          </div>
        </div>

        {/* Question Type Gateway - Always Visible Above Tabs */}
        <div className="px-8 pt-6 pb-4">
          <div className="relative" ref={typeDropdownRef}>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <span>Question Type <span className="text-red-500">*</span></span>
              <Tooltip text="Choose Multiple Choice for single or multi-answer selections, Short Answer for precise text keyword match evaluation, or True/False for binary statements." />
            </label>
            <button
              type="button"
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none text-left flex items-center justify-between bg-white transition-all duration-200 ${
                showTypeDropdown
                  ? 'border-[#10B981] ring-4 ring-[#10B981]/10'
                  : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
              }`}
            >
              <span className="text-[#0F172A] font-medium text-sm">
                {getTypeLabel(question)}
              </span>
              <svg
                className={`w-5 h-5 text-[#64748B] transition-transform duration-200 ${showTypeDropdown ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showTypeDropdown && (
              <div
                className="absolute top-full left-0 right-0 w-full mt-1 z-50 bg-white border border-[#10B981] rounded-lg shadow-lg overflow-hidden"
                style={{
                  boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.15), 0 4px 6px -4px rgba(16, 185, 129, 0.1)'
                }}
              >
                {[
                  { type: 'single-choice', label: 'Single choice', allowMultiple: false },
                  { type: 'multiple-choice', label: 'Multiple choice', allowMultiple: true },
                  { type: 'short-answer', label: 'Short answer' },
                  { type: 'true-false', label: 'True or false' }
                ].map(({ type, label, allowMultiple }) => {
                  const isCurrent =
                    type === 'single-choice' ? ((question.type || 'multiple-choice') === 'multiple-choice' && !question.allowMultipleAnswers) :
                    type === 'multiple-choice' ? ((question.type || 'multiple-choice') === 'multiple-choice' && question.allowMultipleAnswers) :
                    (question.type === type);

                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (type === 'single-choice' || type === 'multiple-choice') {
                          if ((question.type || 'multiple-choice') !== 'multiple-choice') {
                            handleTypeChange('multiple-choice');
                          }
                          onUpdateQuestion({ ...question, type: 'multiple-choice', allowMultipleAnswers: allowMultiple });
                        } else {
                          handleTypeChange(type);
                        }
                        setShowTypeDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors text-sm ${
                        isCurrent
                          ? 'bg-[#F0FDF4] text-[#0F172A] font-medium'
                          : 'hover:bg-[#F1F5F9] text-[#0F172A]'
                      }`}
                    >
                      <span className="flex-1">{label}</span>
                      {isCurrent && (
                        <svg className="w-5 h-5 text-[#10B981]" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Premium Tabbed Navigation Bar - Modern Underline Slate */}
        <div className="px-8 pb-0 border-b border-[#E2E8F0] bg-transparent">
          <div className="flex gap-8 px-1">
            <button
              type="button"
              onClick={() => setActiveTab('content')}
              className={`pb-3 -mb-px text-sm font-semibold transition-colors duration-200 relative flex items-center gap-2 ${
                activeTab === 'content'
                  ? 'text-[#0F172A] border-b-2 border-[#10B981]'
                  : 'text-[#64748B] hover:text-[#334155]'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Content Builder
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`pb-3 -mb-px text-sm font-semibold transition-colors duration-200 relative flex items-center gap-2 ${
                activeTab === 'settings'
                  ? 'text-[#0F172A] border-b-2 border-[#10B981]'
                  : 'text-[#64748B] hover:text-[#334155]'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Scoring & Extras
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('media')}
              className={`pb-3 -mb-px text-sm font-semibold transition-colors duration-200 relative flex items-center gap-2 ${
                activeTab === 'media'
                  ? 'text-[#0F172A] border-b-2 border-[#10B981]'
                  : 'text-[#64748B] hover:text-[#334155]'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Question Image
            </button>
          </div>
        </div>

        {/* Tab Content - Full Width */}
        <div className="p-8">

          {/* TAB 1: Content Builder */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              {/* Question Text with Embedded Counter */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Question Text <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs text-[#64748B]">💡 LaTeX math supported ($x^2$)</span>
                </div>
                <div className="relative w-full">
                  <textarea
                    value={question.questionText}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        onUpdateText(e.target.value);
                      }
                    }}
                    placeholder="Type your question here..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 pb-8 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981] resize-none text-sm"
                  />
                  <span className={`absolute bottom-2 right-3 text-[11px] font-mono pointer-events-none select-none ${
                    question.questionText.length >= 500 ? 'text-red-500' : 'text-[#94A3B8]'
                  }`}>
                    {question.questionText.length}/500
                  </span>
                </div>

                {/* Live Preview - Only show if LaTeX detected */}
                {(question.questionText.includes('$')) && (
                  <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-blue-700">👁️ Preview:</span>
                    </div>
                    <div className="text-sm text-gray-900">
                      <SafeMathText>{question.questionText}</SafeMathText>
                    </div>
                  </div>
                )}
              </div>

              {/* Polymorphic Answer Canvas */}
              <AnswerCanvas
                question={question}
                onUpdateQuestion={onUpdateQuestion}
                onToggleMultipleAnswers={onToggleMultipleAnswers}
                onAddOption={onAddOption}
                onDeleteOption={onDeleteOption}
                onUpdateOption={onUpdateOption}
                onToggleCorrect={onToggleCorrect}
              />
            </div>
          )}

          {/* TAB 2: Scoring & Extras */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Points Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span>Points <span className="text-red-500">*</span></span>
                  <Tooltip text="The baseline point value awarded to a user for identifying the absolute correct answer pattern for this question. Values are restricted between 1 and 10 points." />
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={question.points}
                    onChange={(e) => onUpdatePoints(e.target.value)}
                    min="1"
                    max="10"
                    className="w-24 px-3 py-3 border border-[#E2E8F0] bg-[#F8FAFC] rounded-lg text-center font-semibold text-gray-900 focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981]"
                  />
                  <span className="text-sm text-gray-500 font-medium">pts</span>
                </div>
              </div>

              {/* Explanation with Embedded Counter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span>Explanation (optional)</span>
                  <Tooltip text="This explanation will appear to test takers after they answer the question. Use it to clarify the correct answer or provide additional context that helps them learn from the question." />
                </label>
                <div className="relative w-full">
                  <textarea
                    value={question.explanation || ''}
                    onChange={(e) => {
                      if (e.target.value.length <= 300) {
                        onUpdateExplanation(e.target.value);
                      }
                    }}
                    placeholder="Type answer rationale..."
                    rows={4}
                    maxLength={300}
                    className="w-full px-4 py-3 pb-8 border border-[#E2E8F0] rounded-lg bg-white focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981] resize-vertical text-sm"
                    style={{ minHeight: '100px' }}
                  />
                  <span className={`absolute bottom-2 right-3 text-[11px] font-mono pointer-events-none select-none ${
                    (question.explanation?.length || 0) >= 300 ? 'text-red-500' : 'text-[#94A3B8]'
                  }`}>
                    {question.explanation?.length || 0}/300
                  </span>
                </div>
              </div>

              {/* Hint with Embedded Counter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span>Hint (optional)</span>
                  <Tooltip text="A helpful hint that test takers can reveal if they need guidance. This will be shown when they click the hint button during the quiz to help them find the correct answer." />
                </label>
                <div className="relative w-full">
                  <textarea
                    value={question.hint || ''}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        onUpdateHint(e.target.value);
                      }
                    }}
                    placeholder="Type a helpful hint to guide test takers..."
                    rows={3}
                    maxLength={200}
                    className="w-full px-4 py-3 pb-8 border border-[#E2E8F0] rounded-lg bg-white focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981] resize-vertical text-sm"
                    style={{ minHeight: '80px' }}
                  />
                  <span className={`absolute bottom-2 right-3 text-[11px] font-mono pointer-events-none select-none ${
                    (question.hint?.length || 0) >= 200 ? 'text-red-500' : 'text-[#94A3B8]'
                  }`}>
                    {question.hint?.length || 0}/200
                  </span>
                </div>
              </div>

              {/* Partial Credit Settings (only for multiple-choice with multiple answers) */}
              {question.type === 'multiple-choice' && question.allowMultipleAnswers && (
                <div className="p-4 border-2 border-[#E2E8F0] rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-semibold text-gray-900">
                        Partial Credit
                      </label>
                      <Tooltip text="Allow students to earn partial points for selecting some (but not all) correct answers. Disabled by default - students must select all correct answers to earn points." />
                    </div>

                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => onUpdateQuestion({
                        ...question,
                        partialCreditEnabled: !question.partialCreditEnabled
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        question.partialCreditEnabled ? 'bg-[#10B981]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          question.partialCreditEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Scoring Mode Selection (only shown when partial credit is enabled) */}
                  {question.partialCreditEnabled && (
                    <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-3">
                        Scoring Method
                      </label>

                      <div className="space-y-3">
                        {/* Proportional Mode Card - Whole Card Clickable */}
                        <div
                          onClick={() => onUpdateQuestion({ ...question, partialCreditMode: 'proportional' })}
                          className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer select-none transition-all ${
                            question.partialCreditMode === 'proportional'
                              ? 'border-[#10B981] bg-[#F0FDF4]'
                              : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
                          }`}
                        >
                          {/* Left: Radio + Text */}
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="radio"
                              name={`partial-credit-${question.id}`}
                              value="proportional"
                              checked={question.partialCreditMode === 'proportional'}
                              onChange={() => {}}
                              className="w-4 h-4 mt-0.5 text-[#10B981] cursor-pointer pointer-events-none"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-[#0F172A]">Share points proportionally</div>
                              <div className="text-xs text-[#64748B] mt-1">
                                Students earn split credit for each correct answer they find. No penalties for mistakes.
                              </div>
                            </div>
                          </div>

                          {/* Right: Visual Impact Badge */}
                          <div className="ml-4 flex-shrink-0">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E2E8F0] text-[#334155] rounded-md">
                              <span className="text-xs font-semibold">💡 Example: 2 / 4 correct = 50% points</span>
                            </div>
                          </div>
                        </div>

                        {/* Penalty Mode Card - Whole Card Clickable */}
                        <div
                          onClick={() => onUpdateQuestion({ ...question, partialCreditMode: 'penalty' })}
                          className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer select-none transition-all ${
                            question.partialCreditMode === 'penalty'
                              ? 'border-[#10B981] bg-[#F0FDF4]'
                              : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
                          }`}
                        >
                          {/* Left: Radio + Text */}
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="radio"
                              name={`partial-credit-${question.id}`}
                              value="penalty"
                              checked={question.partialCreditMode === 'penalty'}
                              onChange={() => {}}
                              className="w-4 h-4 mt-0.5 text-[#10B981] cursor-pointer pointer-events-none"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-[#0F172A]">Deduct points for wrong choices</div>
                              <div className="text-xs text-[#64748B] mt-1">
                                Correct choices grant points, but incorrect choices cancel out matching correct ones. Minimum score is 0 (never negative).
                              </div>
                            </div>
                          </div>

                          {/* Right: Alert Badge */}
                          <div className="ml-4 flex-shrink-0">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FEF3C7] text-[#92400E] rounded-md">
                              <span className="text-xs font-semibold">⚠️ Example: 1 Wrong cancels 1 Right</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* TAB 3: Media Cover */}
          {activeTab === 'media' && (
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <span>Media Cover</span>
                <Tooltip text="Attach an optional visual graphic prompt to the question stem. Supports PNG or JPG formats up to a maximum file capacity size of 5MB." />
              </label>

              {!question.imageUrl ? (
                <div className="relative">
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validate file size (5MB max)
                        if (file.size > 5 * 1024 * 1024) {
                          notify({
                            title: 'File Too Large',
                            message: 'Image must be less than 5MB',
                            type: 'error'
                          });
                          return;
                        }

                        // Validate file type
                        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                          notify({
                            title: 'Invalid File Type',
                            message: 'Only PNG and JPG images are supported',
                            type: 'error'
                          });
                          return;
                        }

                        // Convert to base64 or object URL for preview
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          onUpdateImage(reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    id="media-upload"
                  />
                  <label
                    htmlFor="media-upload"
                    className="w-full h-40 border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] rounded-lg flex flex-col items-center justify-center text-gray-600 hover:border-[#10B981] hover:text-[#10B981] hover:bg-[#F0FDF4]/30 transition-all cursor-pointer"
                  >
                    <ImageIcon className="w-12 h-12 mb-3 text-gray-400" />
                    <span className="text-sm font-medium">Click to upload image</span>
                    <span className="text-xs text-gray-400 mt-1">PNG or JPG, max 5MB</span>
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Remove image clicked');
                        onUpdateImage('');
                        onUpdateImageCaption(''); // Clear caption when removing image
                      }}
                      className="text-xs text-red-600 hover:text-red-700 transition-colors font-medium hover:underline"
                    >
                      Remove Image
                    </button>
                  </div>
                  <img
                    src={question.imageUrl}
                    alt="Media preview"
                    className="w-full h-64 object-contain rounded-lg border border-[#E2E8F0] bg-slate-50"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x300?text=Invalid+Image';
                    }}
                  />

                  {/* Image Caption Field */}
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                      Image Caption (optional)
                      <span className="text-gray-400 font-normal">- Shown below the image during quiz</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={question.imageCaption || ''}
                        onChange={(e) => {
                          if (e.target.value.length <= 100) {
                            onUpdateImageCaption(e.target.value);
                          }
                        }}
                        placeholder="e.g., Figure 1: Office workspace, Diagram A, Reference image..."
                        maxLength={100}
                        className="w-full px-4 py-2.5 pr-16 border border-[#E2E8F0] rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-gray-400">
                        {question.imageCaption?.length || 0}/100
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                      Add a descriptive label to help test takers understand the image context
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Navigation Footer - Integrated Inside Card (Wizard Step Navigation) */}
        <div className="border-t border-[#E2E8F0] bg-white px-10 py-6 rounded-b-2xl">
          <div className="flex items-center justify-between">
            {/* Left: Previous Step (Back to Quiz Basics) */}
            <button
              onClick={onPreviousStep}
              className="px-5 py-2.5 border border-[#E2E8F0] rounded-lg text-[#64748B] font-medium transition-all duration-200 hover:border-[#CBD5E1] hover:bg-[#F8FAFC] flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            {/* Right: Next Step (Go to Review) */}
            <button
              onClick={onNextStep}
              className="px-6 py-2.5 bg-[#10B981] hover:bg-[#0D9488] text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Type Switch Confirmation Modal */}
      {showTypeSwitchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-[#E2E8F0]">
              <h3 className="text-xl font-bold text-[#0F172A]">Switch Question Type?</h3>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-[#64748B] leading-relaxed">
                Changing the question format alters the backend data model structure. To prevent losing your current question options, please verify your choice before proceeding.
              </p>
              <p className="text-[#64748B] leading-relaxed mt-3">
                Your question text, points, and explanation will be preserved.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowTypeSwitchModal(false);
                  setPendingType(null);
                }}
                className="px-5 py-2.5 border border-[#E2E8F0] rounded-lg text-[#64748B] font-medium hover:bg-white transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmTypeSwitch}
                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all"
              >
                Switch Type
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Answer Canvas - Polymorphic renderer based on question type
const AnswerCanvas = ({
  question,
  onUpdateQuestion,
  onToggleMultipleAnswers,
  onAddOption,
  onDeleteOption,
  onUpdateOption,
  onToggleCorrect
}) => {
  const questionType = question.type || 'multiple-choice';

  return (
    <div className="mb-6 transition-all duration-200 ease-out">
      {questionType === 'multiple-choice' && (
        <MultipleChoiceCanvas
          question={question}
          onToggleMultipleAnswers={onToggleMultipleAnswers}
          onAddOption={onAddOption}
          onDeleteOption={onDeleteOption}
          onUpdateOption={onUpdateOption}
          onToggleCorrect={onToggleCorrect}
        />
      )}
      {questionType === 'short-answer' && (
        <ShortAnswerCanvas
          question={question}
          onUpdateQuestion={onUpdateQuestion}
        />
      )}
      {questionType === 'true-false' && (
        <TrueFalseCanvas
          question={question}
          onUpdateQuestion={onUpdateQuestion}
        />
      )}
    </div>
  );
};

// Multiple Choice Canvas Component
const MultipleChoiceCanvas = ({
  question,
  onToggleMultipleAnswers,
  onAddOption,
  onDeleteOption,
  onUpdateOption,
  onToggleCorrect
}) => {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-3">
        Answer Options <span className="text-red-500">*</span>
      </label>

      {/* Options List */}
      <div className="space-y-3 mb-4">
        {question.options?.map((option, idx) => (
          <div key={option.id} className="flex items-start gap-3 group">
            {/* Correct Answer Toggle */}
            <input
              type={question.allowMultipleAnswers ? 'checkbox' : 'radio'}
              name={`question-${question.id}`}
              checked={option.isCorrect}
              onChange={() => onToggleCorrect(option.id)}
              className="w-5 h-5 mt-3 text-[#10B981] cursor-pointer flex-shrink-0"
            />

            {/* Option Text Input with Embedded Counter */}
            <div className="flex-1 relative">
              <input
                type="text"
                value={option.text}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    onUpdateOption(option.id, e.target.value);
                  }
                }}
                placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                maxLength={200}
                className={`w-full px-4 py-3 pr-16 border rounded-lg focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 transition-all text-sm ${
                  option.isCorrect ? 'border-[#10B981] bg-green-50 font-medium' : 'border-[#E2E8F0]'
                }`}
              />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono pointer-events-none select-none ${
                option.text.length >= 200 ? 'text-red-500' : 'text-[#94A3B8]'
              }`}>
                {option.text.length}/200
              </span>
            </div>

            {/* Delete Button (shows on hover, min 2 options) */}
            {question.options.length > 2 && (
              <button
                onClick={() => onDeleteOption(option.id)}
                className="p-2 mt-2 opacity-0 group-hover:opacity-100 hover:bg-red-50 text-red-600 rounded-lg transition-all flex-shrink-0"
                title="Delete option"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add Option Button */}
      {question.options?.length < 6 && (
        <button
          onClick={onAddOption}
          className="text-[#10B981] hover:text-[#059669] font-medium text-sm flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Option (max 6)
        </button>
      )}
    </div>
  );
};

// Short Answer Canvas Component
const ShortAnswerCanvas = ({ question, onUpdateQuestion }) => {
  const primaryAnswer = question.correctAnswers?.find(a => a.isPrimary)?.value || '';
  const alternativeAnswers = question.correctAnswers?.filter(a => !a.isPrimary) || [];

  const updatePrimaryAnswer = (value) => {
    const newAnswers = [
      { value, isPrimary: true },
      ...alternativeAnswers
    ];
    onUpdateQuestion({ ...question, correctAnswers: newAnswers });
  };

  const addAlternativeAnswer = () => {
    if (alternativeAnswers.length >= 5) return;

    const newAnswers = [
      { value: primaryAnswer, isPrimary: true },
      ...alternativeAnswers,
      { value: '', isPrimary: false }
    ];
    onUpdateQuestion({ ...question, correctAnswers: newAnswers });
  };

  const updateAlternativeAnswer = (index, value) => {
    const newAlternatives = [...alternativeAnswers];
    newAlternatives[index] = { value, isPrimary: false };

    const newAnswers = [
      { value: primaryAnswer, isPrimary: true },
      ...newAlternatives
    ];
    onUpdateQuestion({ ...question, correctAnswers: newAnswers });
  };

  const deleteAlternativeAnswer = (index) => {
    const newAlternatives = alternativeAnswers.filter((_, i) => i !== index);
    const newAnswers = [
      { value: primaryAnswer, isPrimary: true },
      ...newAlternatives
    ];
    onUpdateQuestion({ ...question, correctAnswers: newAnswers });
  };

  const updateMatchingMode = (mode) => {
    onUpdateQuestion({ ...question, matchingMode: mode });
  };

  return (
    <div>
      {/* Primary Answer */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Correct answer <span className="text-red-500">*</span>
        </label>
        <div className="relative w-full">
          <input
            type="text"
            value={primaryAnswer}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                updatePrimaryAnswer(e.target.value);
              }
            }}
            placeholder="e.g., mitochondria"
            maxLength={100}
            className="w-full px-4 py-3 pr-16 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981] text-sm"
          />
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono pointer-events-none select-none ${
            primaryAnswer.length >= 100 ? 'text-red-500' : 'text-[#94A3B8]'
          }`}>
            {primaryAnswer.length}/100
          </span>
        </div>
      </div>

      {/* Alternative Answers */}
      {alternativeAnswers.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Alternative answers (optional)
          </label>
          <div className="space-y-2">
            {alternativeAnswers.map((answer, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={answer.value}
                    onChange={(e) => {
                      if (e.target.value.length <= 100) {
                        updateAlternativeAnswer(idx, e.target.value);
                      }
                    }}
                    placeholder={`Alternative ${idx + 1}`}
                    maxLength={100}
                    className="w-full px-4 py-2.5 pr-16 border border-[#E2E8F0] bg-[#F8FAFC] rounded-lg focus:outline-none focus:ring-4 focus:ring-[#10B981]/10 focus:border-[#10B981] text-sm"
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono pointer-events-none select-none ${
                    answer.value.length >= 100 ? 'text-red-500' : 'text-[#94A3B8]'
                  }`}>
                    {answer.value.length}/100
                  </span>
                </div>
                <button
                  onClick={() => deleteAlternativeAnswer(idx)}
                  className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                  title="Delete alternative"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Alternative Button */}
      {alternativeAnswers.length < 5 && (
        <button
          onClick={addAlternativeAnswer}
          className="text-[#10B981] hover:text-[#059669] font-medium text-sm flex items-center gap-2 mb-4 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Alternative Answer
        </button>
      )}

      {/* Matching Mode */}
      <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Matching mode
        </label>
        <div className="space-y-2">
          {[
            { value: 'exact-case-insensitive', label: 'Exact match (case-insensitive)', description: 'Default - "Mitochondria" matches "mitochondria"' },
            { value: 'contains', label: 'Contains keyword', description: 'Matches if answer contains the keyword' },
            { value: 'exact-case-sensitive', label: 'Exact match (case-sensitive)', description: 'Strict - "Mitochondria" does NOT match "mitochondria"' }
          ].map(({ value, label, description }) => (
            <div key={value} className="flex items-start gap-3">
              <input
                type="radio"
                name={`matching-mode-${question.id}`}
                value={value}
                checked={question.matchingMode === value}
                onChange={() => updateMatchingMode(value)}
                className="w-4 h-4 mt-0.5 text-[#10B981] cursor-pointer"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 cursor-pointer" onClick={() => updateMatchingMode(value)}>
                  {label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// True/False Canvas Component
const TrueFalseCanvas = ({ question, onUpdateQuestion }) => {
  const selectAnswer = (value) => {
    onUpdateQuestion({ ...question, correctAnswer: value });
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-3">
        Select the correct answer <span className="text-red-500">*</span>
      </label>

      {/* Segmented Control Container - Premium iOS-Style Design */}
      <div className="inline-flex p-1 bg-[#F1F5F9] rounded-lg border border-[#E2E8F0]">
        <button
          type="button"
          onClick={() => selectAnswer(true)}
          className={`relative px-8 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 ${
            question.correctAnswer === true
              ? 'bg-white text-[#10B981] shadow-sm'
              : 'text-[#64748B] hover:text-[#0F172A]'
          }`}
        >
          <span className="flex items-center gap-2">
            {question.correctAnswer === true && (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            <span>True</span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => selectAnswer(false)}
          className={`relative px-8 py-2.5 rounded-md font-semibold text-sm transition-all duration-200 ${
            question.correctAnswer === false
              ? 'bg-white text-[#10B981] shadow-sm'
              : 'text-[#64748B] hover:text-[#0F172A]'
          }`}
        >
          <span className="flex items-center gap-2">
            {question.correctAnswer === false && (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            <span>False</span>
          </span>
        </button>
      </div>

      {/* Validation Hint */}
      {question.correctAnswer === null && (
        <p className="text-xs text-gray-500 mt-3 italic flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Select the correct answer
        </p>
      )}
    </div>
  );
};

// STEP 3: Review & Publish
const ReviewStep = ({
  quiz,
  isPublic,
  setIsPublic,
  shuffleQuestions,
  setShuffleQuestions,
  randomizeOptions,
  setRandomizeOptions,
  hasTimeLimit,
  setHasTimeLimit,
  timeLimit,
  setTimeLimit,
  navigationType,
  setNavigationType,
  reviewPermission,
  setReviewPermission,
  attemptLimit,
  setAttemptLimit,
  maxAttempts,
  setMaxAttempts,
  hasPassingScore,
  setHasPassingScore,
  passingScore,
  setPassingScore,
  onPreviousStep,
  badgeState,
  onPublish,
  loading,
  isEditMode
}) => {
  return (
    <div className="w-full max-w-[1120px] mx-auto">
      {/* Global Top Shell - Back + Title + Badge */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] mb-4">
        <div className="px-10 py-4">
          <div className="flex items-center justify-between w-full">
            {/* Left: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button
                onClick={onPreviousStep}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#64748B]" />
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-[#0F172A] tracking-tight">
                  Configure & Publish
                </h2>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
              </div>
            </div>

            {/* Right: Draft Badge */}
            <div
              className="px-3 py-1.5 rounded-full text-[13px] font-semibold uppercase tracking-wide border flex items-center gap-1.5"
              style={{
                backgroundColor: badgeState.bg,
                borderColor: badgeState.border,
                color: badgeState.color
              }}
            >
              <span>{badgeState.icon}</span>
              <span>{badgeState.text}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card - Stepper + Content + Footer */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0]">
        {/* Wizard Stepper Section */}
        <div className="px-10 pt-10 pb-0">
          {/* Wizard Stepper */}
          <div className="py-6 mb-0">
            <div className="flex items-start justify-center gap-4">
              {[
                { num: 1, label: '1. Quiz Basics' },
                { num: 2, label: '2. Building Questions' },
                { num: 3, label: '3. Configure & Publish' }
              ].map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex flex-col items-center flex-1 max-w-[140px]">
                    <div
                      className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                        idx === 2
                          ? 'bg-[#10B981] text-white shadow-md shadow-emerald-200'
                          : 'bg-[#10B981] text-white'
                      }`}
                    >
                      {idx < 2 ? '✓' : step.num}
                    </div>
                    <span
                      className={`text-[13px] mt-2 text-center leading-tight transition-colors ${
                        idx === 2
                          ? 'text-[#0F172A] font-semibold'
                          : 'text-[#94A3B8] font-normal'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className="relative flex-shrink-0" style={{ width: '60px', height: '32px' }}>
                      <div
                        className="absolute h-0.5 bg-[#10B981]"
                        style={{
                          top: '16px',
                          left: 0,
                          right: 0
                        }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Divider Below Stepper */}
            <div className="w-full h-px bg-[#E2E8F0] mt-5"></div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-10 pt-6 pb-10 space-y-6">

        {/* Summary */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <h3 className="font-bold text-gray-900 mb-3">{quiz.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{quiz.description}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-md">
              <div className="text-2xl font-bold text-[#0F172A]">{quiz.totalQuestions}</div>
              <div className="text-xs text-[#64748B]">Questions</div>
            </div>
            <div className="text-center p-3 bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-md">
              <div className="text-2xl font-bold text-[#0F172A]">{quiz.totalPoints}</div>
              <div className="text-xs text-[#64748B]">Total Points</div>
            </div>
          </div>
        </div>

        {/* Privacy Setting - Streamlined Toggle */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                Quiz Visibility
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Privacy Control</p>
                  <p className="text-gray-300">Public quizzes appear in search and category feeds. Private quizzes are only visible to you and invited users.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Switch - Active in Step 3 */}
            <button
              type="button"
              onClick={() => setIsPublic(!isPublic)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isPublic ? 'bg-[#10B981]' : 'bg-gray-300'
              }`}
              aria-label={isPublic ? 'Make quiz private' : 'Make quiz public'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isPublic ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Status Text */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isPublic ? 'bg-[#10B981]' : 'bg-gray-400'}`}></div>
            <p className="text-xs text-gray-600">
              {isPublic ? (
                <span>
                  <span className="font-semibold text-[#10B981]">Public</span> - Visible in search and feeds
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-gray-700">Private</span> - Only you can access
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Shuffle Questions Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                Shuffle Questions
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Question Order</p>
                  <p className="text-gray-300">Questions will appear in random order for each student to prevent cheating.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              type="button"
              onClick={() => setShuffleQuestions(!shuffleQuestions)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                shuffleQuestions ? 'bg-[#10B981]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  shuffleQuestions ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Status Text */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${shuffleQuestions ? 'bg-[#10B981]' : 'bg-gray-400'}`}></div>
            <p className="text-xs text-gray-600">
              {shuffleQuestions ? (
                <span>
                  <span className="font-semibold text-[#10B981]">Enabled</span> - Questions appear in random order for each student
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-gray-700">Disabled</span> - Questions appear in the order you created them
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Randomize Options Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                Randomize Option Order
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Randomize Answer Options</p>
                  <p className="text-gray-300">Answer options will be shuffled in random order for each student to prevent memorization by position.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              type="button"
              onClick={() => setRandomizeOptions(!randomizeOptions)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                randomizeOptions ? 'bg-[#10B981]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  randomizeOptions ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Status Text */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${randomizeOptions ? 'bg-[#10B981]' : 'bg-gray-400'}`}></div>
            <p className="text-xs text-gray-600">
              {randomizeOptions ? (
                <span>
                  <span className="font-semibold text-[#10B981]">Enabled</span> - Answer choices shuffled for each student
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-gray-700">Disabled</span> - Answer choices stay in the order you set
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Time Limit Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                Time Limit
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Quiz Timer</p>
                  <p className="text-gray-300">Set a time limit for completing the quiz. Students will see a countdown timer.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              type="button"
              onClick={() => setHasTimeLimit(!hasTimeLimit)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                hasTimeLimit ? 'bg-[#10B981]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  hasTimeLimit ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Status & Duration Input (Combined) */}
          {hasTimeLimit ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3 pt-2 border-t border-[#E2E8F0]">
                <label className="text-sm text-gray-600 font-medium">Duration:</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={timeLimit}
                    onChange={(e) => setTimeLimit(Math.max(1, Math.min(180, parseInt(e.target.value) || 1)))}
                    min="1"
                    max="180"
                    className="w-20 px-3 py-2 border-2 border-[#E2E8F0] rounded-lg text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                <p className="text-xs text-gray-600">
                  <span className="font-semibold text-[#10B981]">Enabled</span> - Students have {timeLimit} {timeLimit === 1 ? 'minute' : 'minutes'} to complete
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gray-400"></div>
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-gray-700">Disabled</span> - Students can take as long as they need
              </p>
            </div>
          )}
        </div>

        {/* Navigation Type Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                Question Navigation
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Navigation Mode</p>
                  <p className="text-gray-300">Choose whether students can jump between questions freely or must answer them in order.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              type="button"
              onClick={() => setNavigationType(navigationType === 'free' ? 'sequential' : 'free')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                navigationType === 'free' ? 'bg-[#10B981]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  navigationType === 'free' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Status Text */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${navigationType === 'free' ? 'bg-[#10B981]' : 'bg-gray-400'}`}></div>
            <p className="text-xs text-gray-600">
              {navigationType === 'free' ? (
                <span>
                  <span className="font-semibold text-[#10B981]">Free mode</span> - Students can jump to any question
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-gray-700">Sequential mode</span> - Students answer in order
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Review Permission Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-semibold text-gray-900">
                After Quiz Review
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Review Settings</p>
                  <p className="text-gray-300">Control what students can see after submitting the quiz: full feedback with correct answers, just their score, or nothing at all.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Radio Options */}
            <div className="space-y-2.5">
              {/* Full Review */}
              <div
                onClick={() => setReviewPermission('full')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  reviewPermission === 'full'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="reviewPermission"
                  value="full"
                  checked={reviewPermission === 'full'}
                  onChange={() => setReviewPermission('full')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    Full Review (Recommended)
                  </div>
                  <div className="text-xs text-gray-600">
                    Students can see all questions, their answers, correct answers, and explanations
                  </div>
                </div>
              </div>

              {/* Partial Review (Score Only) */}
              <div
                onClick={() => setReviewPermission('partial')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  reviewPermission === 'partial'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="reviewPermission"
                  value="partial"
                  checked={reviewPermission === 'partial'}
                  onChange={() => setReviewPermission('partial')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    Score Only
                  </div>
                  <div className="text-xs text-gray-600">
                    Students only see their final score/percentage, no question details
                  </div>
                </div>
              </div>

              {/* No Review */}
              <div
                onClick={() => setReviewPermission('none')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  reviewPermission === 'none'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="reviewPermission"
                  value="none"
                  checked={reviewPermission === 'none'}
                  onChange={() => setReviewPermission('none')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    No Review
                  </div>
                  <div className="text-xs text-gray-600">
                    Students cannot see any results or feedback after submission
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Passing Score Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-semibold text-gray-900">
                Passing Score
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Pass/Fail Threshold</p>
                  <p className="text-gray-300">Set a minimum percentage students must achieve to pass. Students who fail will see a different message and won't have access to review (if review is set to "Score Only" or "No Review").</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Toggle Enable/Disable */}
            <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border-2 border-[#E2E8F0] mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-0.5">
                  Enable Passing Score
                </div>
                <div className="text-xs text-gray-600">
                  Require students to achieve a minimum score to pass
                </div>
              </div>
              <button
                type="button"
                onClick={() => setHasPassingScore(!hasPassingScore)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  hasPassingScore ? 'bg-emerald-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hasPassingScore ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Passing Score Input - Only show when enabled */}
            {hasPassingScore && (
              <div className="p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Minimum Passing Percentage
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={passingScore}
                    onChange={(e) => setPassingScore(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    style={{
                      background: `linear-gradient(to right, #10B981 0%, #10B981 ${passingScore}%, #E5E7EB ${passingScore}%, #E5E7EB 100%)`
                    }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={passingScore}
                    onChange={(e) => {
                      const value = Math.max(0, Math.min(100, Number(e.target.value)));
                      setPassingScore(value);
                    }}
                    className="w-20 px-3 py-2 border-2 border-emerald-300 rounded-lg text-center font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Students need to score {passingScore}% or higher to pass this quiz
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Attempt Limit Setting */}
        <div className="p-4 border-2 border-[#E2E8F0] rounded-lg">
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-semibold text-gray-900">
                Attempt Limit
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute left-0 top-6 z-20 w-64 p-3 bg-[#0F172A] text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-normal">
                  <p className="font-semibold mb-1 text-white">Attempt Restrictions</p>
                  <p className="text-gray-300">Control how many times students can take this quiz. Use this to prevent unlimited practice or to create high-stakes assessments.</p>
                  <div className="absolute -top-1 left-2 w-2 h-2 bg-[#0F172A] transform rotate-45"></div>
                </div>
              </div>
            </div>

            {/* Radio Options */}
            <div className="space-y-2.5">
              {/* Unlimited */}
              <div
                onClick={() => setAttemptLimit('unlimited')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  attemptLimit === 'unlimited'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="attemptLimit"
                  value="unlimited"
                  checked={attemptLimit === 'unlimited'}
                  onChange={() => setAttemptLimit('unlimited')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    Unlimited (Recommended)
                  </div>
                  <div className="text-xs text-gray-600">
                    Students can retake the quiz as many times as they want for practice
                  </div>
                </div>
              </div>

              {/* One Time Only */}
              <div
                onClick={() => setAttemptLimit('once')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  attemptLimit === 'once'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="attemptLimit"
                  value="once"
                  checked={attemptLimit === 'once'}
                  onChange={() => setAttemptLimit('once')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    One Time Only
                  </div>
                  <div className="text-xs text-gray-600">
                    Students can only take the quiz once - ideal for exams and assessments
                  </div>
                </div>
              </div>

              {/* Custom Limit */}
              <div
                onClick={() => setAttemptLimit('custom')}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  attemptLimit === 'custom'
                    ? 'border-[#10B981] bg-emerald-50'
                    : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'
                }`}
              >
                <input
                  type="radio"
                  name="attemptLimit"
                  value="custom"
                  checked={attemptLimit === 'custom'}
                  onChange={() => setAttemptLimit('custom')}
                  className="w-4 h-4 mt-0.5 cursor-pointer accent-[#10B981]"
                  style={{ accentColor: '#10B981' }}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 mb-0.5">
                    Custom Limit
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Set a specific number of attempts allowed
                  </div>

                  {/* Custom Number Input */}
                  {attemptLimit === 'custom' && (
                    <div className="flex items-center gap-2 mt-2">
                      <label className="text-xs text-gray-700 font-medium">
                        Max attempts:
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={maxAttempts}
                        onChange={(e) => setMaxAttempts(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        onClick={(e) => e.stopPropagation()}
                        className="w-16 px-2 py-1 border border-[#E2E8F0] rounded text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent"
                      />
                      <span className="text-xs text-gray-500">
                        (1-10 attempts)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Footer Actions */}
      <div className="px-10 py-6 border-t border-[#E2E8F0] bg-white rounded-b-2xl flex items-center justify-between gap-4">
        <button
          onClick={onPreviousStep}
          disabled={loading}
          className="group px-5 py-2.5 border-2 border-slate-300 rounded-lg text-slate-700 font-medium transition-all hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-0.5" />
          <span>Previous</span>
        </button>

        <button
          onClick={onPublish}
          disabled={loading}
          className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{isEditMode ? 'Update' : 'Create'}</span>
            </>
          )}
        </button>
      </div>
    </div>
    </div>
  );
};

export default MakeQuiz;
